import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Briefcase, Building2, MapPin, Cpu, Calendar, User, Plus, Trash2, ChevronRight } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import Breadcrumbs from "@/components/Breadcrumbs";
import JobStatusBadge from "@/components/JobStatusBadge";
import MetaCard from "@/components/MetaCard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EntityCombobox, EntityMultiCombobox } from "@/components/ui/EntityCombobox";
import { formatDate } from "@/lib/format";
import { readBack } from "@/lib/nav";

const SLOT_TYPES = [
  { value: "am", label: "AM" },
  { value: "pm", label: "PM" },
  { value: "all_day", label: "All Day" },
  { value: "custom", label: "Custom" },
];

const EMPTY = {
  customer_id: "",
  site_id: "",
  machine_ids: [],
  engineer_id: "",
  title: "",
  description: "",
  status: "new",
  priority: "normal",
  duration_estimate: "half_day",
  scheduled_slots: [],
  site_access_notes_snapshot: "",
};

function uid() { return Math.random().toString(36).slice(2) + Date.now().toString(36); }

export default function JobForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const backCtx = readBack(searchParams);

  const [form, setForm] = useState(EMPTY);
  const [meta, setMeta] = useState({}); // job_number, created_at, updated_at, created_by
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [machines, setMachines] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [siteAccessSource, setSiteAccessSource] = useState("");
  const [overrideAccess, setOverrideAccess] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  // Capture URL prefill ONCE at mount — apply lazily as each list loads,
  // otherwise Radix Select drops the value before its SelectItems mount.
  const prefill = useMemo(() => ({
    customer_id: searchParams.get("customer_id") || null,
    site_id: searchParams.get("site_id") || null,
    machine_id: searchParams.get("machine_id") || null,
  }), []);
  const prefillAppliedRef = useRef({ customer: false, site: false, machine: false });

  // Load supporting data
  useEffect(() => {
    Promise.all([
      api.get("/customers", { params: { active: true, limit: 500 } }),
      api.get("/jobs/engineers"),
    ]).then(([{ data: c }, { data: e }]) => {
      setCustomers(c.items);
      setEngineers(e);
    }).catch((err) => toast.error(formatApiError(err)));
  }, [isEdit]);

  // Apply prefill AFTER each list is in state — SelectItems are mounted, so Radix Select value syncs.
  useEffect(() => {
    if (isEdit) return;
    const cid = prefill.customer_id;
    if (cid && !prefillAppliedRef.current.customer && customers.some((x) => x.id === cid)) {
      prefillAppliedRef.current.customer = true;
      setForm((f) => (f.customer_id ? f : { ...f, customer_id: cid }));
    }
  }, [customers, prefill.customer_id, isEdit]);

  useEffect(() => {
    if (isEdit) return;
    const sid = prefill.site_id;
    if (sid && !prefillAppliedRef.current.site && sites.some((s) => s.id === sid)) {
      prefillAppliedRef.current.site = true;
      setForm((f) => (f.site_id ? f : { ...f, site_id: sid }));
    }
  }, [sites, prefill.site_id, isEdit]);

  useEffect(() => {
    if (isEdit) return;
    const mid = prefill.machine_id;
    if (mid && !prefillAppliedRef.current.machine && machines.some((m) => m.id === mid)) {
      prefillAppliedRef.current.machine = true;
      setForm((f) => (f.machine_ids.includes(mid) ? f : { ...f, machine_ids: [...f.machine_ids, mid] }));
    }
  }, [machines, prefill.machine_id, isEdit]);

  // Load sites/machines when customer/site changes
  useEffect(() => {
    if (!form.customer_id) { setSites([]); return; }
    api.get("/sites", { params: { customer_id: form.customer_id, active: true, limit: 500 } })
      .then(({ data }) => setSites(data.items))
      .catch((err) => toast.error(formatApiError(err)));
  }, [form.customer_id]);

  useEffect(() => {
    if (!form.site_id) { setMachines([]); setSiteAccessSource(""); return; }
    api.get("/machines", { params: { site_id: form.site_id, active: true, limit: 500 } })
      .then(({ data }) => setMachines(data.items))
      .catch((err) => toast.error(formatApiError(err)));
    // fetch site for access notes source
    api.get(`/sites/${form.site_id}`).then(({ data }) => setSiteAccessSource(data.access_notes || "")).catch(() => {});
  }, [form.site_id]);

  // Load job in edit mode
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/jobs/${id}`);
        setForm({
          customer_id: data.customer_id,
          site_id: data.site_id,
          machine_ids: data.machine_ids || [],
          engineer_id: data.engineer_id || "",
          title: data.title || "",
          description: data.description || "",
          status: data.status,
          priority: data.priority || "normal",
          duration_estimate: data.duration_estimate || "half_day",
          scheduled_slots: (data.scheduled_slots || []).map((s) => ({ ...s, uid: uid() })),
          site_access_notes_snapshot: data.site_access_notes_snapshot || "",
        });
        setMeta({
          job_number: data.job_number,
          created_at: data.created_at,
          updated_at: data.updated_at,
          created_by: data.created_by,
        });
        // override if snapshot differs from site's current access notes
        if ((data.site_access_notes_snapshot || "") !== (data.site?.access_notes || "")) {
          setOverrideAccess(true);
        }
      } catch (err) {
        toast.error(formatApiError(err));
        navigate("/jobs", { replace: true });
      } finally { setLoading(false); }
    })();
  }, [id, isEdit, navigate]);

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const onChangeCustomer = (v) => update({ customer_id: v, site_id: "", machine_ids: [] });
  const onChangeSite = (v) => {
    update({ site_id: v, machine_ids: [] });
    // reset access override on site change
    setOverrideAccess(false);
    setForm((prev) => ({ ...prev, site_access_notes_snapshot: "" }));
  };

  const toggleMachine = (mid) => setForm((prev) => {
    const next = new Set(prev.machine_ids);
    if (next.has(mid)) next.delete(mid); else next.add(mid);
    return { ...prev, machine_ids: Array.from(next) };
  });

  // Slots
  const addSlot = () => update({
    scheduled_slots: [
      ...form.scheduled_slots,
      { uid: uid(), date: "", slot_type: "all_day", start_time: "", end_time: "", notes: "" },
    ],
  });
  const removeSlot = (i) => update({ scheduled_slots: form.scheduled_slots.filter((_, idx) => idx !== i) });
  const updateSlot = (i, patch) => update({
    scheduled_slots: form.scheduled_slots.map((s, idx) => (idx === i ? { ...s, ...patch } : s)),
  });

  // Auto-suggest title
  const suggestedTitle = useMemo(() => {
    if (form.title) return form.title;
    const picked = machines.filter((m) => form.machine_ids.includes(m.id));
    if (!picked.length) return "";
    const first = picked[0];
    const extra = picked.length - 1;
    return `Service — ${[first.make, first.model].filter(Boolean).join(" ") || "machine"}` + (extra > 0 ? ` (+${extra} more)` : "");
  }, [form.title, form.machine_ids, machines]);

  const canScheduleNow = form.engineer_id && form.scheduled_slots.length > 0 && form.scheduled_slots.every((s) => s.date);

  const buildPayload = useCallback((targetStatus) => {
    const payload = {
      customer_id: form.customer_id,
      site_id: form.site_id,
      machine_ids: form.machine_ids,
      engineer_id: form.engineer_id || null,
      title: form.title || suggestedTitle || "Service",
      description: form.description,
      status: targetStatus || form.status,
      priority: form.priority,
      duration_estimate: form.duration_estimate,
      scheduled_slots: form.scheduled_slots.map(({ uid: _u, ...rest }) => rest),
      site_access_notes_snapshot: overrideAccess ? (form.site_access_notes_snapshot || "") : siteAccessSource,
    };
    return payload;
  }, [form, suggestedTitle, overrideAccess, siteAccessSource]);

  const submit = async (e, targetStatus) => {
    if (e && e.preventDefault) e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const payload = buildPayload(targetStatus);
      if (isEdit) {
        const { data } = await api.patch(`/jobs/${id}`, payload);
        toast.success(`${data.job_number} updated`);
        navigate(backCtx?.to || `/jobs/${data.id}`);
      } else {
        const { data } = await api.post(`/jobs`, payload);
        toast.success(`${data.job_number} created`);
        navigate(backCtx?.to || `/jobs/${data.id}`);
      }
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

  const cancelTo = backCtx?.to || (isEdit ? `/jobs/${id}` : "/jobs");

  const machineTypes = new Set(machines.filter((m) => form.machine_ids.includes(m.id)).map((m) => m.machine_type_id));
  const mixedTypes = machineTypes.size > 1;

  return (
    <div className="p-6 space-y-6" data-testid="job-form-page">
      <Breadcrumbs
        items={[
          { label: "Jobs", to: "/jobs" },
          backCtx
            ? { label: backCtx.label, to: backCtx.to }
            : { label: "All Jobs", to: "/jobs" },
          ...(isEdit ? [{ label: meta.job_number || "Job", to: `/jobs/${id}` }, { label: "Edit" }] : [{ label: "New" }]),
        ]}
      />
      <PageHeader
        testId="job-form-header"
        title={isEdit ? `Edit ${meta.job_number}` : "New job"}
        subtitle={<span className="flex items-center gap-2">{isEdit && <JobStatusBadge status={form.status} />} <span>{isEdit ? form.title : "Draft — save to generate job number."}</span></span>}
        actions={<Button asChild variant="outline" size="sm" data-testid="job-form-back"><Link to={cancelTo}><ArrowLeft className="h-4 w-4" /> Back</Link></Button>}
      />

      <form onSubmit={(e) => submit(e)} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Card 1 — Details */}
          <Card className="rounded-md">
            <CardHeader><CardTitle className="text-sm font-medium flex items-center gap-2"><Briefcase className="h-4 w-4 text-primary" /> Details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={form.title}
                  onChange={(e) => update({ title: e.target.value })}
                  placeholder={suggestedTitle || "e.g. Service — Krones Contifill 90"}
                  data-testid="job-form-title"
                />
                {!form.title && suggestedTitle && (
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Suggested: {suggestedTitle}</div>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="desc">Description</Label>
                <Textarea id="desc" rows={4} value={form.description} onChange={(e) => update({ description: e.target.value })} placeholder="Brief for the engineer…" data-testid="job-form-description" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="priority">Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => update({ priority: v })}>
                    <SelectTrigger id="priority" data-testid="job-form-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="duration">Duration estimate</Label>
                  <Select value={form.duration_estimate} onValueChange={(v) => update({ duration_estimate: v })}>
                    <SelectTrigger id="duration" data-testid="job-form-duration"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="half_day">Half Day</SelectItem>
                      <SelectItem value="all_day">All Day</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Card 2 — Customer & Site */}
          <Card className="rounded-md">
            <CardHeader><CardTitle className="text-sm font-medium flex items-center gap-2"><Building2 className="h-4 w-4 text-primary" /> Customer &amp; Site</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="cust">Customer</Label>
                  <EntityCombobox
                    items={customers}
                    value={form.customer_id || ""}
                    onChange={onChangeCustomer}
                    placeholder="Select customer"
                    emptyMessage="No customers match."
                    testid="job-form-customer"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="site">Site</Label>
                  <EntityCombobox
                    items={sites}
                    value={form.site_id || ""}
                    onChange={onChangeSite}
                    placeholder={form.customer_id ? "Select site" : "Pick a customer first"}
                    emptyMessage="No sites match."
                    disabled={!form.customer_id}
                    testid="job-form-site"
                  />
                </div>
              </div>

              {form.site_id && (
                <div className="rounded-sm border border-border bg-muted/30 p-3 space-y-2" data-testid="job-form-access-block">
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Site access notes</div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Override for this job</span>
                      <Switch
                        checked={overrideAccess}
                        onCheckedChange={(v) => {
                          setOverrideAccess(v);
                          if (v && !form.site_access_notes_snapshot) update({ site_access_notes_snapshot: siteAccessSource });
                        }}
                        data-testid="job-form-override-access"
                      />
                    </div>
                  </div>
                  {overrideAccess ? (
                    <Textarea
                      rows={3}
                      value={form.site_access_notes_snapshot}
                      onChange={(e) => update({ site_access_notes_snapshot: e.target.value })}
                      data-testid="job-form-access-snapshot"
                    />
                  ) : (
                    <div className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">
                      {siteAccessSource || <span className="italic">No access notes on the site.</span>}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Card 3 — Machines */}
          <Card className="rounded-md">
            <CardHeader><CardTitle className="text-sm font-medium flex items-center gap-2"><Cpu className="h-4 w-4 text-primary" /> Machines</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {!form.site_id ? (
                <div className="text-xs text-muted-foreground italic">Select a site to see its machines.</div>
              ) : machines.length === 0 ? (
                <div className="text-xs text-muted-foreground italic" data-testid="job-form-machines-empty">No active machines at this site.</div>
              ) : (
                <>
                  <EntityMultiCombobox
                    items={machines}
                    value={form.machine_ids}
                    onChange={(ids) => update({ machine_ids: ids })}
                    placeholder="Search machines…"
                    emptyMessage="No machines match."
                    getLabel={(m) => `${m.make || ""} ${m.model || ""}`.trim() || "Machine"}
                    getSecondary={(m) => `Serial ${m.serial || "—"}`}
                    testid="job-form-machines"
                  />
                  {form.machine_ids.length > 0 && (
                    <ul className="space-y-1.5" data-testid="job-form-machines-list">
                      {machines.filter((m) => form.machine_ids.includes(m.id)).map((m) => (
                        <li
                          key={m.id}
                          data-testid={`job-form-machine-${m.id}`}
                          className="flex items-center justify-between gap-3 border border-primary/40 bg-primary/5 rounded-sm px-2.5 py-1.5"
                        >
                          <div>
                            <div className="text-sm font-medium">{m.make} {m.model}</div>
                            <div className="text-xs text-muted-foreground font-mono">Serial {m.serial}</div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => toggleMachine(m.id)}
                            data-testid={`job-form-machine-remove-${m.id}`}
                          >
                            Remove
                          </Button>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
              {mixedTypes && (
                <div className="text-xs text-amber-700 bg-amber-500/10 border border-amber-500/20 rounded-sm p-2" data-testid="job-form-mixed-types-warning">
                  Selected machines span multiple types. This is allowed but worth confirming.
                </div>
              )}
            </CardContent>
          </Card>

          {/* Card 4 — Schedule */}
          <Card className="rounded-md">
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm font-medium flex items-center gap-2"><Calendar className="h-4 w-4 text-primary" /> Schedule</CardTitle>
              <Button type="button" size="sm" variant="outline" onClick={addSlot} data-testid="job-form-add-slot"><Plus className="h-3.5 w-3.5" /> Add slot</Button>
            </CardHeader>
            <CardContent>
              {form.scheduled_slots.length === 0 ? (
                <div className="text-xs text-muted-foreground italic" data-testid="job-form-slots-empty">
                  No slots yet. Add one or more to schedule the job (supports multi-day, e.g. Fri + Mon).
                </div>
              ) : (
                <ul className="space-y-2">
                  {form.scheduled_slots.map((s, i) => (
                    <li key={s.uid || i} data-testid={`job-form-slot-${i}`} className="grid grid-cols-12 gap-2 items-center border border-border rounded-sm p-2 bg-card">
                      <Input type="date" value={s.date} onChange={(e) => updateSlot(i, { date: e.target.value })} className="col-span-3 h-9 text-xs" data-testid={`job-form-slot-${i}-date`} />
                      <Select value={s.slot_type} onValueChange={(v) => updateSlot(i, { slot_type: v })}>
                        <SelectTrigger className="col-span-2 h-9 text-xs" data-testid={`job-form-slot-${i}-type`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {SLOT_TYPES.map((t) => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}
                        </SelectContent>
                      </Select>
                      {s.slot_type === "custom" ? (
                        <>
                          <Input type="time" value={s.start_time} onChange={(e) => updateSlot(i, { start_time: e.target.value })} className="col-span-2 h-9 text-xs" data-testid={`job-form-slot-${i}-start`} />
                          <Input type="time" value={s.end_time} onChange={(e) => updateSlot(i, { end_time: e.target.value })} className="col-span-2 h-9 text-xs" data-testid={`job-form-slot-${i}-end`} />
                        </>
                      ) : (<div className="col-span-4" />)}
                      <Input value={s.notes} onChange={(e) => updateSlot(i, { notes: e.target.value })} placeholder="Notes" className="col-span-2 h-9 text-xs" data-testid={`job-form-slot-${i}-notes`} />
                      <Button type="button" variant="ghost" size="icon" className="col-span-1 h-8 w-8" onClick={() => removeSlot(i)} data-testid={`job-form-slot-${i}-remove`}><Trash2 className="h-4 w-4" /></Button>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          {/* Card 5 — Assignment */}
          <Card className="rounded-md" id="assign">
            <CardHeader><CardTitle className="text-sm font-medium flex items-center gap-2"><User className="h-4 w-4 text-primary" /> Assignment</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="eng">Engineer</Label>
                <EntityCombobox
                  items={engineers}
                  value={form.engineer_id || ""}
                  onChange={(v) => update({ engineer_id: v })}
                  placeholder="Unassigned"
                  emptyMessage="No engineers match."
                  getLabel={(e) => e.full_name}
                  getSecondary={(e) => e.username}
                  allowClear
                  clearLabel="Unassigned"
                  testid="job-form-engineer"
                />
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  Optional as a draft. Required to move status to Scheduled.
                </div>
              </div>
              <div className="text-xs text-muted-foreground rounded-sm border border-dashed border-border p-3">
                Availability conflicts will surface here once the Dispatch Board (Phase 4) ships.
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right meta column */}
        <div className="space-y-4">
          <Card className="rounded-md">
            <CardHeader><CardTitle className="text-sm font-medium">Status</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground uppercase tracking-wider font-bold">Current</span>
                <JobStatusBadge status={form.status} />
              </div>
              {form.status === "new" && (
                <Button
                  type="button"
                  className="w-full"
                  size="sm"
                  disabled={!canScheduleNow || saving}
                  onClick={(e) => submit(e, "scheduled")}
                  data-testid="job-form-move-to-scheduled"
                >
                  Save &amp; move to Scheduled
                </Button>
              )}
              {!canScheduleNow && form.status === "new" && (
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Requires engineer + ≥1 slot</div>
              )}
            </CardContent>
          </Card>

          <MetaCard
            title="Metadata"
            fields={[
              { label: "Job #", value: meta.job_number || <span className="text-muted-foreground italic">on save</span>, testId: "job-form-meta-number" },
              { label: "Created", value: meta.created_at ? formatDate(meta.created_at) : "—" },
              { label: "Updated", value: meta.updated_at ? formatDate(meta.updated_at) : "—" },
              { label: "Created by", value: meta.created_by ? (meta.created_by === user?.id ? user?.full_name : meta.created_by) : (user?.full_name || "—") },
            ]}
          />

          <div className="flex items-center justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => navigate(cancelTo)} data-testid="job-form-cancel">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="job-form-submit"><Save className="h-4 w-4" />{saving ? "Saving…" : isEdit ? "Save" : "Save draft"}</Button>
          </div>
        </div>
      </form>
    </div>
  );
}
